import { createApi } from "@reduxjs/toolkit/query/react";

import { type IApiResponse } from "@/shared/interfaces/api-response.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";

import type {
  ListDriveItemsResponse,
  CreateDriveFolderRequest,
  CreateDriveFolderResponse,
  InitDriveUploadRequest,
  InitDriveUploadResponse,
  CompleteDriveUploadRequest,
  RenameDriveNodeRequest,
  MoveDriveNodeRequest,
  SetDriveNodeVisibilityRequest,
  GetDriveFileViewUrlResponse,
} from "./drive.interface";

const driveApi = createApi({
  reducerPath: "driveApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.Drive, ApiTags.Schools],
  endpoints: (builder) => ({
    listDriveItems: builder.query<
      ListDriveItemsResponse,
      {
        districtId: string;
        path?: string;
        sortBy?: "name" | "updatedAt" | "createdAt" | "sizeBytes" | "type";
        sortOrder?: "asc" | "desc";
      }
    >({
      query: ({ districtId, path = "/", sortBy = "name", sortOrder = "asc" }) => ({
        url: `/districts/${districtId}/drive`,
        method: "GET",
        params: { path, sortBy, sortOrder },
      }),
      providesTags: [ApiTags.Drive],
      transformResponse: (result: IApiResponse<ListDriveItemsResponse>) =>
        result.data,
    }),
    getDriveFileViewUrl: builder.query<
      GetDriveFileViewUrlResponse,
      { districtId: string; fileId: string }
    >({
      query: ({ districtId, fileId }) => ({
        url: `/districts/${districtId}/drive/files/${fileId}/view-url`,
        method: "GET",
      }),
      transformResponse: (result: IApiResponse<GetDriveFileViewUrlResponse>) =>
        result.data,
    }),
    createDriveFolder: builder.mutation<
      CreateDriveFolderResponse,
      { districtId: string; path?: string } & CreateDriveFolderRequest
    >({
      query: ({ districtId, path, ...body }) => ({
        url: `/districts/${districtId}/drive/folders`,
        method: "POST",
        params: path ? { path } : undefined,
        body,
      }),
      invalidatesTags: [ApiTags.Drive],
    }),
    initDriveUpload: builder.mutation<
      InitDriveUploadResponse,
      { districtId: string; path?: string } & InitDriveUploadRequest
    >({
      query: ({ districtId, path, ...body }) => ({
        url: `/districts/${districtId}/drive/uploads/init`,
        method: "POST",
        params: path ? { path } : undefined,
        body,
      }),
      transformResponse: (result: IApiResponse<InitDriveUploadResponse>) =>
        result.data,
    }),
    completeDriveUpload: builder.mutation<
      void,
      { districtId: string } & CompleteDriveUploadRequest
    >({
      query: ({ districtId, ...body }) => ({
        url: `/districts/${districtId}/drive/uploads/complete`,
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.Drive, ApiTags.Schools],
    }),
    renameDriveNode: builder.mutation<
      void,
      { districtId: string; nodeId: string } & RenameDriveNodeRequest
    >({
      query: ({ districtId, nodeId, ...body }) => ({
        url: `/districts/${districtId}/drive/nodes/${nodeId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [ApiTags.Drive, ApiTags.Schools],
    }),
    moveDriveNode: builder.mutation<
      void,
      { districtId: string; nodeId: string } & MoveDriveNodeRequest
    >({
      query: ({ districtId, nodeId, ...body }) => ({
        url: `/districts/${districtId}/drive/nodes/${nodeId}/move`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [ApiTags.Drive, ApiTags.Schools],
    }),
    setDriveNodeVisibility: builder.mutation<
      void,
      { districtId: string; nodeId: string } & SetDriveNodeVisibilityRequest
    >({
      query: ({ districtId, nodeId, ...body }) => ({
        url: `/districts/${districtId}/drive/nodes/${nodeId}/visibility`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [ApiTags.Drive],
    }),
    deleteDriveNode: builder.mutation<
      void,
      { districtId: string; nodeId: string }
    >({
      query: ({ districtId, nodeId }) => ({
        url: `/districts/${districtId}/drive/nodes/${nodeId}`,
        method: "DELETE",
      }),
      invalidatesTags: [ApiTags.Drive],
    }),
  }),
});

export const {
  useListDriveItemsQuery,
  useLazyGetDriveFileViewUrlQuery,
  useCreateDriveFolderMutation,
  useInitDriveUploadMutation,
  useCompleteDriveUploadMutation,
  useRenameDriveNodeMutation,
  useMoveDriveNodeMutation,
  useSetDriveNodeVisibilityMutation,
  useDeleteDriveNodeMutation,
} = driveApi;

export default driveApi;
